import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Download as DlIcon, Smartphone } from "lucide-react";
import { API, fmtDate } from "@/lib/api";

const LABELS = { pdf: "A4 PDF guide", epub: "Ebook (EPUB) — Apple Books, Play Books, Kindle", toolkit: "Interactive toolkit (download)", html: "Phone & web edition (download)", attachment: "Download file" };
const READ = { html: "Read now — phone & web edition", toolkit: "Open the interactive toolkit" };

export default function Download() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/d/${token}`).then((r) => setD(r.data)).catch((e) => setError(e.response?.data?.detail || "This link is not valid."));
  }, [token]);

  return (
    <div className="min-h-screen bg-[#FAF7F0] flex items-center justify-center p-3 sm:p-6">
      <div className="w-full max-w-xl bg-[#FFFDF9] border border-[#2A2325]/10 shadow-sm relative px-6 py-10 sm:p-14 text-center rise" data-testid="download-page">
        <div className="absolute inset-3 border border-[#5E2B38]/25 pointer-events-none" />
        {d?.has_logo ? (
          <img src={`${API}/public/logo`} alt="Soft Spine Press" className="h-16 mx-auto object-contain" />
        ) : (
          <div className="text-[#5E2B38] text-xl">❦</div>
        )}
        <div className="text-[10px] tracking-[0.34em] text-[#5E2B38] mt-4">{(d?.brand_name || "SOFT SPINE PRESS").toUpperCase()}</div>
        {error ? (
          <p className="font-body-serif italic text-lg text-[#5C5255] mt-10" data-testid="download-error">{error}</p>
        ) : d ? (
          <>
            <div className="eyebrow mt-10">{d.series || d.category}</div>
            <h1 className="font-display text-4xl sm:text-5xl leading-tight mt-3" data-testid="download-title">{d.title}</h1>
            {d.subtitle && <p className="font-body-serif italic text-[#5C5255] mt-3">{d.subtitle}</p>}
            <p className="font-body-serif mt-8">{d.customer_name ? `Thank you, ${d.customer_name}.` : "Thank you for your purchase."} Your copy is ready.</p>
            {d.available.some((k) => READ[k]) && (
              <div className="mt-8 space-y-3" data-testid="download-read-now">
                {["html", "toolkit"].filter((k) => d.available.includes(k)).map((k) => (
                  <a key={k} href={`${API}/public/d/${token}/file/${k}?view=true`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-3 px-5 sm:px-6 py-4 min-h-[56px] bg-[#B08D57] text-[#2A2325] hover:bg-[#9C7B49] transition-colors text-left" data-testid={`read-${k}-btn`}>
                    <span className="tracking-wide text-sm font-medium">{READ[k]}</span>
                    <Smartphone size={17} className="shrink-0" />
                  </a>
                ))}
                <p className="text-xs text-[#5C5255]">Opens in your browser and fits any phone. Add it to your home screen to keep it handy.</p>
              </div>
            )}
            <div className="mt-6 space-y-3">
              {d.available.map((k) => (
                <a key={k} href={`${API}/public/d/${token}/file/${k}`} className="flex items-center justify-between gap-3 px-5 sm:px-6 py-4 min-h-[56px] text-left bg-[#5E2B38] text-[#FFFDF9] hover:bg-[#471F2A] transition-colors" data-testid={`download-${k}-btn`}>
                  <span className="tracking-wide text-sm">{k === "attachment" ? d.attachment_name : LABELS[k]}</span>
                  <DlIcon size={16} className="shrink-0" />
                </a>
              ))}
            </div>
            <p className="text-xs text-[#5C5255] mt-8" data-testid="download-meta">
              {d.expires_at ? <>{d.downloads_left} downloads left · link active until {fmtDate(d.expires_at)}</> : "Your companion downloads for this edition — bookmark this page."}
            </p>
          </>
        ) : (
          <p className="font-display italic text-xl text-[#5C5255] mt-10">Fetching your copy…</p>
        )}
        <div className="text-xs text-[#5C5255] mt-10">{d?.website || "softspinepress.com"}</div>
      </div>
    </div>
  );
}
